import { ClientSideRowModelModule } from "@ag-grid-community/client-side-row-model";
import type {
  ColDef,
  GetRowIdFunc,
  RowSelectedEvent,
  SortChangedEvent,
} from "@ag-grid-community/core";
import { AgGridReact } from "@ag-grid-community/react";
import {
  Button,
  Callout,
  Card,
  ControlGroup,
  InputGroup,
  MenuItem,
} from "@blueprintjs/core";
import { Select } from "@blueprintjs/select";
import { type AnyApiGridQueryStateValue } from "@package/ui-redux";
import { debounce } from "lodash";
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ITEM_GRID_QUERY_SEARCH_DEBOUNCE_MS } from "../../constants/constants";
import { ItemGridFilterPanel } from "../ItemGridFilterPanel/ItemGridFilterPanel";
import {
  ItemGridPagination,
  type ItemGridPaginationProps,
} from "../ItemGridPagination/ItemGridPagination";
import {
  createColDefCheckmark,
  type ItemGridSortOrder,
} from "./item-grid-utils";

export type ItemGridProps<T> = {
  columnDefs: ColDef[];
  data?: T[];
  dataLoading?: boolean;
  dataError?: boolean;
  filter?: AnyApiGridQueryStateValue["variables"]["filter"];
  filterPanels?: ReactNode[];
  filtersOpen?: boolean;
  filtersSupported?: boolean;
  includeDeleted?: boolean;
  onGetRowId: (row: T) => string;
  onFilterChanged?: (
    filter: AnyApiGridQueryStateValue["variables"]["filter"],
  ) => void;
  onFilterOpenChanged?: (open: boolean) => void;
  onIncludeDeletedChanged?: (include: boolean) => void;
  onQueryChanged?: (query: string) => void;
  onSelectedItemIdChanged?: (id?: string) => void;
  onSortFieldChanged?: (field: string) => void;
  onSortOrderChanged?: (order: ItemGridSortOrder) => void;
  pagination: ItemGridPaginationProps;
  query?: string;
};

export function ItemGrid<T>({
  columnDefs,
  data,
  dataLoading = true,
  dataError = false,
  filter,
  filterPanels,
  filtersOpen = false,
  filtersSupported = false,
  includeDeleted = false,
  onGetRowId,
  onFilterChanged,
  onFilterOpenChanged,
  onIncludeDeletedChanged,
  onQueryChanged,
  onSelectedItemIdChanged,
  onSortFieldChanged,
  onSortOrderChanged,
  pagination,
  query = "",
}: ItemGridProps<T>) {
  const [localQueryValue, setLocalQueryValue] = useState<string>(query);

  const columnDefsAdjusted = useMemo<ColDef[]>(() => {
    if (includeDeleted) {
      return [...columnDefs, createColDefCheckmark("deleted", "Deleted")];
    }

    return columnDefs;
  }, [columnDefs, includeDeleted]);

  const hasFiltersApplied = useMemo<boolean>(() => {
    return !!filter && Object.keys(filter).length > 0;
  }, [filter]);

  const debouncedUpdateQuery = useRef(
    debounce((query: string) => {
      onQueryChanged?.(query);
      pagination.onPageChanged?.(0);
    }, ITEM_GRID_QUERY_SEARCH_DEBOUNCE_MS),
  );

  const getRowId: GetRowIdFunc<T> = useCallback(
    (params) => {
      return onGetRowId(params.data);
    },
    [onGetRowId],
  );

  const onGridReady = useCallback(() => {
    onSelectedItemIdChanged?.(undefined);
  }, [onSelectedItemIdChanged]);

  const onRowSelected = useCallback(
    (event: RowSelectedEvent<T>) => {
      const selected = event.api.getSelectedNodes();
      if (selected?.length === 1 && selected[0]?.data) {
        onSelectedItemIdChanged?.(onGetRowId(selected[0].data));
      } else {
        onSelectedItemIdChanged?.(undefined);
      }
    },
    [onGetRowId, onSelectedItemIdChanged],
  );

  const onSortChanged = useCallback(
    (event: SortChangedEvent<T>) => {
      const sorted = event.api.getColumnState().find((col) => !!col.sort);

      onSortOrderChanged?.(sorted?.sort === "desc" ? "DESC" : "ASC");
      onSortFieldChanged?.(sorted?.colId ?? "id");
    },
    [onSortFieldChanged, onSortOrderChanged],
  );

  useEffect(
    function effectOnLocalQueryValueChanged() {
      debouncedUpdateQuery.current(localQueryValue);
    },
    [debouncedUpdateQuery, localQueryValue],
  );

  useEffect(
    function effectOnQueryChanged() {
      setLocalQueryValue(query);
    },
    [query],
  );

  if (dataError) {
    return (
      <Callout intent="danger" title="Failed Loading Items" icon="warning-sign">
        <p>
          The request to retrieve the selected content failed. Please refresh
          the application, or try logging back in.
        </p>
      </Callout>
    );
  }

  return (
    <div className="flex flex-col">
      <ControlGroup className="mb-2" fill vertical={false}>
        <InputGroup
          large
          fill
          value={localQueryValue}
          placeholder="Filter for items (case insensitive)"
          inputClassName="placeholder:italic"
          onValueChange={(value) => setLocalQueryValue(value)}
        />
        <Select<boolean>
          activeItem={!!includeDeleted}
          filterable={false}
          items={[false, true]}
          itemRenderer={(
            isDeleted,
            { handleClick, handleFocus, modifiers },
          ) => (
            <MenuItem
              active={modifiers.active}
              disabled={modifiers.disabled}
              key={isDeleted ? "yes" : "no"}
              onClick={handleClick}
              onFocus={handleFocus}
              roleStructure="listoption"
              text={isDeleted ? "Include Deleted" : "Active Only"}
            />
          )}
          popoverProps={{ minimal: true }}
          onItemSelect={(isDeleted) => onIncludeDeletedChanged?.(isDeleted)}
        >
          <Button
            alignText="left"
            className="whitespace-nowrap"
            fill
            large
            minimal
            outlined
            rightIcon="caret-down"
            text={includeDeleted ? "Include Deleted" : "Active Only"}
          />
        </Select>
        {filtersSupported && (
          <>
            <Button
              active={filtersOpen}
              alignText="left"
              icon="filter"
              intent={hasFiltersApplied ? "warning" : "none"}
              large
              minimal
              outlined
              onClick={() => onFilterOpenChanged?.(!filtersOpen)}
            />
            <Button
              alignText="left"
              icon="reset"
              large
              minimal
              outlined
              onClick={() => onFilterChanged?.({})}
            />
          </>
        )}
      </ControlGroup>
      <Card className="flex flex-row m-0 p-0" elevation={1}>
        <div className="grow h-full item-grid ag-theme-quartz">
          <AgGridReact<T>
            cellSelection={false}
            columnDefs={columnDefsAdjusted}
            domLayout="autoHeight"
            getRowId={getRowId}
            loading={dataLoading}
            modules={[ClientSideRowModelModule]}
            rowData={data}
            rowModelType="clientSide"
            rowSelection={{
              mode: "singleRow",
              enableClickSelection: true,
              checkboxes: true,
            }}
            onGridReady={onGridReady}
            onRowSelected={onRowSelected}
            onSortChanged={onSortChanged}
          />
        </div>
        {filtersOpen && (
          <div className="flex w-64 min-h-64 items-stretch border-l border-slate-200 border-solid">
            <ItemGridFilterPanel
              filter={filter}
              onFilterChanged={onFilterChanged}
            >
              {filterPanels as unknown as ReactNode[]}
            </ItemGridFilterPanel>
          </div>
        )}
      </Card>
      <ItemGridPagination {...pagination} />
    </div>
  );
}
